import * as React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import {
  Button,
  ButtonColor,
  ButtonVariant,
  Modal,
  ModalWidth,
  Spinner,
  SpinnerColor,
  Text,
  TextVariant,
} from "../../ui";
import { Field } from "../EditVehicleDialog/Field";

const buttonStyle: React.CSSProperties = { minHeight: 48, textTransform: "none", fontWeight: 600 };

/** Edits only the vehicle's notes, from the Specs card. */
export const EditNotesDialog: React.FC<{
  open: boolean;
  notes: string | null;
  onCancel: () => void;
  /** Rejects when the save fails; the dialog then stays open with the text and says so. */
  onSave: (notes: string) => Promise<void>;
}> = ({ open, notes, onCancel, onSave }) => {
  const [value, setValue] = useState(notes ?? "");
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const wasOpen = useRef(false);

  // Each opening starts from the saved notes; a re-read while typing must not reset them.
  useLayoutEffect(() => {
    if (open && !wasOpen.current) {
      setValue(notes ?? "");
      setSaveFailed(false);
    }
    wasOpen.current = open;
  }, [open, notes]);

  const changed = value.trim() !== (notes ?? "").trim();
  const save = async () => {
    setSaving(true);
    setSaveFailed(false);
    try {
      await onSave(value);
    } catch {
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onCancel}
      fullWidth
      maxWidth={ModalWidth.XS}
      aria-labelledby="edit-notes-title"
      // Same look as the mileage dialog: lighter than the page, blue top edge, darker backdrop.
      surfaceStyle={{
        backgroundColor: "#2b3a4f",
        border: "1px solid #64748b",
        borderTop: "4px solid #3b82f6",
        borderRadius: 12,
        boxShadow: "0 16px 40px rgba(0, 0, 0, 0.6)",
        margin: 16,
        width: "calc(100% - 32px)",
      }}
      backdropStyle={{ backgroundColor: "rgba(2, 6, 23, 0.78)" }}
    >
      <div className="flex flex-col gap-4 p-4">
        <Text id="edit-notes-title" variant={TextVariant.H6} style={{ fontSize: 18, fontWeight: 700 }}>
          Vehicle notes
        </Text>
        <Field field="notes" label="Notes" value={value} onChange={setValue} multiline />
        {saveFailed && (
          <Text role="alert" variant={TextVariant.BODY2} className="text-red-300">
            The notes could not be saved. Your text is kept; try again in a moment.
          </Text>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Button variant={ButtonVariant.OUTLINED} onClick={onCancel} disabled={saving} style={buttonStyle}>
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.CONTAINED}
            color={ButtonColor.PRIMARY}
            onClick={save}
            disabled={saving || !changed}
            startIcon={saving ? <Spinner size={18} color={SpinnerColor.INHERIT} aria-hidden /> : undefined}
            style={buttonStyle}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
